import { HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject, of, EMPTY, timer } from 'rxjs';
import { tap, map, catchError, concatMap, filter, take } from 'rxjs/operators';
import { AsyncTaskService } from './async-task.service';
import { MediaLibraryService } from './media-library.service';
import { MediaLabelAiService } from './media-label-ai.service';

export interface MediaUploadResponse {
  fileName: string;
  fileUrl: string;
  fileUuid: string;
  mediaAssetId: number;
  mediaAssetUuid?: string;
}

/** 202 body of POST /api/media/file/upload/async */
interface AsyncUploadAccepted {
  processUuid: string;
  status: string;
}

export interface UploadSuccessEvent {
  response: MediaUploadResponse;
  fileType: string;
  project?: string;
}

export type FileUploadStatus =
  | 'pending'
  | 'uploading' // byte transfer to highp-media
  | 'processing' // server-side pipeline (validate/convert/thumbnails/create asset), tracked via SSE task
  | 'converting' // legacy alias kept for old callers
  | 'success'
  | 'error';

export interface FileUploadItem {
  file: File;
  id: string;
  name: string;
  size: number;
  type: string;
  preview?: string;
  status: FileUploadStatus;
  progress: number;
  error?: string;
  fileUuid?: string;
  mediaAssetId?: number;
  /** highp-media processUuid — key into AsyncTaskService for stage/progress. */
  taskId?: string;
}

export interface UploadProgressEvent {
  fileId: string;
  progress: number;
  status: FileUploadStatus;
  error?: string;
  response?: MediaUploadResponse;
  /** Present from 'processing' on — the async task id carrying stage details. */
  taskId?: string;
}

/**
 * THE single media upload service of the app. Byte transfer goes to highp-media's
 * async endpoint (202 + processUuid); the server-side pipeline (validate → convert →
 * thumbnails → create media asset, stages differ per type) is then tracked through
 * AsyncTaskService via SSE, so the upload dialog can be closed and progress lives
 * in the global task toast. On each finished upload the media library silently
 * refreshes (refresh$), which pulls the fresh compliance label with it.
 */
@Injectable({
  providedIn: 'root',
})
export class MediaUploadService {
  private readonly http = inject(HttpClient);
  private readonly asyncTaskService = inject(AsyncTaskService);
  private readonly mediaLibraryService = inject(MediaLibraryService);
  private readonly mediaLabelAi = inject(MediaLabelAiService);

  private uploadSuccess = new Subject<UploadSuccessEvent>();
  private uploadProgress = new Subject<UploadProgressEvent>();

  uploadSuccess$ = this.uploadSuccess.asObservable();
  uploadProgress$ = this.uploadProgress.asObservable();

  uploadFile(file: File, project?: string): Observable<MediaUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (project) {
      formData.append('project', project);
    }

    // Add filename as query parameter to preserve original name
    const encodedFilename = encodeURIComponent(file.name);
    const url = `/api/media/file/upload?name=${encodedFilename}`;

    // Do NOT set Authorization header manually — the AuthorizationInterceptor
    // injects the token automatically and handles 401 refresh + retry.
    return this.http.post<MediaUploadResponse>(url, formData).pipe(
      tap((response) => {
        this.uploadSuccess.next({
          response,
          fileType: this.getFileType(file.name),
          project,
        });
      }),
    );
  }

  getMediaAssetUrl(value: string): string {
    return `/mediaassets/${value}`;
  }

  isImageFile(filename: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return imageExtensions.includes(ext);
  }

  isVideoFile(filename: string): boolean {
    const videoExtensions = ['.mp4', '.webm'];
    const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return videoExtensions.includes(ext);
  }

  isPdfFile(filename: string): boolean {
    return filename.toLowerCase().endsWith('.pdf');
  }

  isValidFileType(filename: string): boolean {
    return this.isImageFile(filename) || this.isVideoFile(filename) || this.isPdfFile(filename);
  }

  uploadMultipleFiles(files: FileUploadItem[], project?: string): Observable<void> {
    return of(...files).pipe(
      concatMap((fileItem) => {
        // Update status to uploading
        this.uploadProgress.next({
          fileId: fileItem.id,
          progress: 0,
          status: 'uploading',
        });

        return this.uploadFileWithProgress(fileItem.file, fileItem.id, project).pipe(
          catchError((error) => {
            // Handle individual file upload error
            const errorMessage =
              error?.error?.message ||
              (error?.status ? `Error ${error.status}` : null) ||
              error?.message ||
              'Upload failed';
            this.uploadProgress.next({
              fileId: fileItem.id,
              progress: 100,
              status: 'error',
              error: errorMessage,
            });
            return EMPTY; // Continue with next file
          }),
        );
      }),
      map(() => void 0), // Convert to void
    );
  }

  /**
   * Byte transfer to POST /api/media/file/upload/async (202 + processUuid), then
   * hand the processUuid to AsyncTaskService which streams the server-side
   * pipeline stages via SSE. The returned observable completes at the 202 —
   * the next file's byte transfer starts while this one is still processing
   * server-side. Terminal state arrives through the task callback.
   */
  private uploadFileWithProgress(file: File, fileId: string, project?: string): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);
    if (project) {
      formData.append('project', project);
    }

    // Add filename as query parameter to preserve original name
    const encodedFilename = encodeURIComponent(file.name);
    const url = `/api/media/file/upload/async?name=${encodedFilename}`;

    // Do NOT set Authorization header manually — the AuthorizationInterceptor
    // injects the token automatically and handles 401 refresh + retry.
    return this.http
      .post<AsyncUploadAccepted>(url, formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event) => {
          if (event.type === HttpEventType.UploadProgress) {
            const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
            this.uploadProgress.next({
              fileId,
              progress,
              status: 'uploading',
            });
            return null;
          } else if (event.type === HttpEventType.Response) {
            const accepted = event.body as AsyncUploadAccepted;
            this.trackServerProcessing(accepted.processUuid, file, fileId, project);
            return accepted;
          }
          return null;
        }),
        filter((accepted): accepted is AsyncUploadAccepted => accepted !== null),
        take(1),
        map(() => void 0),
      );
  }

  /** Register the highp-media process with the async task service (SSE stage
   * stream + global toast) and bridge its terminal state back into the upload
   * dialog's per-file events + the media library refresh. */
  private trackServerProcessing(
    processUuid: string,
    file: File,
    fileId: string,
    project?: string,
  ): void {
    this.uploadProgress.next({
      fileId,
      progress: 0,
      status: 'processing',
      taskId: processUuid,
    });

    this.asyncTaskService.startMediaUpload(processUuid, file.name);
    this.asyncTaskService.setTaskCallback(processUuid, (task) => {
      if (task.status === 'completed') {
        const response = task.result as MediaUploadResponse;
        this.uploadProgress.next({
          fileId,
          progress: 100,
          status: 'success',
          response,
          taskId: processUuid,
        });
        this.uploadSuccess.next({
          response,
          fileType: this.getFileType(file.name),
          project,
        });
        // Silent background refresh — only has an effect while the media
        // library page is open; the reloaded rows carry the compliance label.
        this.mediaLibraryService.refresh$.next();
        this.followAutoLabeling(response, file.name);
      } else {
        this.uploadProgress.next({
          fileId,
          progress: 100,
          status: 'error',
          error: task.error || 'Upload processing failed',
          taskId: processUuid,
        });
      }
    });
  }

  /**
   * With `autoOnUpload` the backend labels the new asset by itself, in a batch nobody subscribed to.
   * Ask for that batch and register it, so the run shows progress (SSE) and its result like a manual one.
   * The job is created inside the upload's final "Creating media asset" step, so it exists by now; one
   * retry covers a slow commit. No run found = nothing to show.
   */
  private followAutoLabeling(response: MediaUploadResponse, name: string): void {
    const assetUuid = response.mediaAssetUuid ?? response.fileUuid;
    if (!assetUuid) return;
    this.mediaLabelAi
      .autoLabelsOnUpload$()
      .pipe(
        take(1),
        filter(Boolean),
        concatMap(() => this.mediaLabelAi.activeBatchOf(assetUuid)),
        concatMap((batch) => (batch ? of(batch) : timer(1500).pipe(concatMap(() => this.mediaLabelAi.activeBatchOf(assetUuid))))),
      )
      .subscribe((batch) => {
        if (batch) this.asyncTaskService.startMediaAutolabel(batch, 1, name);
      });
  }

  private getFileType(filename: string): string {
    if (this.isImageFile(filename)) {
      return 'image';
    } else if (this.isVideoFile(filename)) {
      return 'video';
    } else if (this.isPdfFile(filename)) {
      return 'pdf';
    }
    return 'unknown';
  }
}
